import React, {
  useState, useImperativeHandle, useEffect, useRef, forwardRef,
} from 'react';
import { useTranslation } from 'react-i18next';
import {
  DistributorConfigFieldUpdateCallback,
  DistributorsConfigFormTemplateFormSectionName, FormTemplate, FormTemplateSection, FormTemplateValidator,
} from 'data/template/template.types';
import { DistributorConfiguration } from 'data/distributorConfiguration/distributorConfiguration.types';
import { DistributorData } from 'data/distributor/distributor.types';
import { NodeData } from 'data/nodes/nodes.types';
import { SecurityInfo } from 'data/securityInfo/securityInfo.types';
import { showError } from 'shared/toaster';
import cloneDeep from 'lodash.clonedeep';
import { TlsProxy } from 'data/tlsProxy/tlsProxy.types';
import Form, { ChangedField } from 'shared/form';
import { TextSuggestType } from 'shared/form/custom/TextSuggest/TextSuggest';
import { validateCurrentIpPortCombination, validateIpPortCombinations } from 'utils/validators/ipPortCombinationValidators';
import { SupportedFeaturesAccessFunctions } from 'data/supportedFeatures/supportedFeatures.types';
import { TranslationKey } from 'i18n/i18n.types';
import MainForm from './Main';
import { Ref as MainRef } from './Main/MainForm';
import { ListenersRef } from './Listeners/Listeners';
import { DestinationsRef } from './Destinations/Destinations';
import Listeners from './Listeners';
import Destinations from './Destinations';
import * as adapters from '../../utils/distributorConfigTemplate.adapter';
import { handleInterfacesFieldUpdate } from '../../utils/distributorConfigInterfaceTabFieldUpdate';
import './distributor-config-form.scss';
import newDistributorConfiguration from '../../newDistributorConfiguration';

export type DistributorConfigFormRef = {
  isValid: (forceValidation: boolean, forceTouched: boolean) => boolean
  getValues: () => DistributorConfiguration
}

type Props = {
  template: FormTemplate,
  distributorConfig: DistributorConfiguration,
  distributors: DistributorData,
  nodes: NodeData[],
  securityInfos: SecurityInfo[],
  isEditing: boolean,
  tlsProxies: TlsProxy[],
  availableAccessFunction: SupportedFeaturesAccessFunctions,
  enabledAccessFunction: SupportedFeaturesAccessFunctions,
  i18nKeyPrefix?: string,
  onFieldUpdate?: (
    field: ChangedField,
    templateSection: DistributorsConfigFormTemplateFormSectionName,
    tabIndex: number,
  ) => void;
  onFieldCreateNew?: DistributorConfigFieldUpdateCallback
}

export const DISTRIBUTOR_I18N_KEY = 'ss8Distributor.form';

const DistributorConfigForm = forwardRef<DistributorConfigFormRef, Props>(({
  isEditing,
  template,
  distributorConfig,
  distributors,
  nodes,
  securityInfos,
  tlsProxies,
  availableAccessFunction,
  enabledAccessFunction,
  i18nKeyPrefix = DISTRIBUTOR_I18N_KEY,
  onFieldUpdate,
  onFieldCreateNew,
}: Props, ref) => {
  const [formData, setFormData] = useState(distributorConfig);
  const [nodeNameIpAddressOptions, setNodeNameIpAddressOptions] = useState<TextSuggestType[]>([]);
  const [destinationsTemplate, setDestinationsTemplate] = useState<FormTemplateSection>(
    template.destinations as FormTemplateSection,
  );
  const mainFormRef = useRef<MainRef>(null);
  const listenerRefs = useRef<ListenersRef>(null);
  const destinationRefs = useRef<DestinationsRef>(null);
  const { t } = useTranslation();

  useEffect(() => {
    setFormData({ ...distributorConfig });
  }, [distributorConfig]);

  useEffect(() => {
    setNodeNameIpAddressOptions(adapters.adaptNodeNameIpAddressOptions(distributorConfig.nodeName || '', nodes));
  }, [distributorConfig, nodes]);

  useEffect(() => {
    const newDestinationsTemplate = adapters.adaptDestinationsIpAddressPortTemplate(
      distributorConfig.moduleName,
      template.destinations as FormTemplateSection,
    );

    setDestinationsTemplate(newDestinationsTemplate);
  }, [template, distributorConfig]);

  const isValid = (forceValidation = false, forceTouched = true) => {
    const isMainFormValid = mainFormRef.current?.isValid(forceValidation, forceTouched) ?? true;

    const listeners = listenerRefs.current?.getValues();
    const destinations = destinationRefs.current?.getValues();

    const isListenersValid = listenerRefs.current?.isValid(forceValidation, forceTouched) ?? true;
    const isDestinationsValid = destinationRefs.current?.isValid(forceValidation, forceTouched) ?? true;

    const isIpPortCombinationsValid = validateIpPortCombinations(listeners, destinations);

    if (!isIpPortCombinationsValid) {
      showError(t('ss8Distributor.error.mustBeUnique'));
    }

    return isMainFormValid && isListenersValid && isDestinationsValid && isIpPortCombinationsValid;
  };

  const getValues = (): DistributorConfiguration => {
    const values = mainFormRef.current?.getValues();
    if (values != null) {
      (values as DistributorConfiguration).listeners = listenerRefs.current?.getValues() ?? [];
      (values as DistributorConfiguration).destinations = destinationRefs.current?.getValues() ?? [];
    }
    return values as DistributorConfiguration;
  };

  useImperativeHandle(ref, () => ({
    isValid,
    getValues,
  }));

  const customValidator = (
    _propFields?: unknown,
    _stateFields?: unknown,
    _value?: string,
    _lastSavedValue?: string,
    validator?: FormTemplateValidator,
    form?: Form | undefined,
  ) => {
    const { listeners, destinations } = getValues();

    const valid = validateCurrentIpPortCombination(form?.getValues(), listeners, destinations);
    let message = '';
    if (!valid && validator?.customValidationFunction && validator?.errorMessage) {
      message = t(`${i18nKeyPrefix}.fieldError.${validator.customValidationFunction}` as TranslationKey) ?? '';
    }

    return { isValid: valid, errorMessage: message };
  };

  const customValidationFunctionMap = {
    ipPortTransportCombinationValidator: customValidator,
  };

  const mainFormFieldUpdateHandler = ({ name, value }: ChangedField) => {
    const newFormData = cloneDeep(getValues());
    newFormData[name as keyof DistributorConfiguration] = value;

    if (name === 'moduleName') {
      newFormData.afType = '';
      const newDestinationsTemplate = adapters.adaptDestinationsIpAddressPortTemplate(
        newFormData.moduleName,
        template.destinations as FormTemplateSection,
      );
      setDestinationsTemplate(newDestinationsTemplate);

      if (newFormData.destinations.length === 0) {
        newFormData.destinations = cloneDeep(newDistributorConfiguration.destinations);
      }

    } else if (name === 'afType' || name === 'instanceId') {
      if (name === 'instanceId') {
        newFormData.afType = '';
      }

      const newDestinationsFqdnTemplate = adapters.adaptDestinationsFqdnTemplate(
        newFormData.moduleName,
        newFormData.afType,
        template.destinations as FormTemplateSection,
        distributors.afTagsToProtocolMap,
      );

      setDestinationsTemplate(newDestinationsFqdnTemplate);
      newFormData.destinations = cloneDeep(newDistributorConfiguration.destinations);

    } else if (name === 'nodeName') {
      setNodeNameIpAddressOptions(adapters.adaptNodeNameIpAddressOptions(name, nodes));
    }

    handleInterfacesFieldUpdate(newFormData, name);
    setFormData(newFormData);
  };

  return (
    <div>
      <MainForm
        ref={mainFormRef}
        formData={formData}
        templateSection={template.main as FormTemplateSection}
        distributors={distributors}
        nodes={nodes}
        availableAccessFunction={availableAccessFunction}
        enabledAccessFunction={enabledAccessFunction}
        fieldUpdateHandler={mainFormFieldUpdateHandler}
        i18nKeyPrefix={i18nKeyPrefix}
      />
      <Listeners
        ref={listenerRefs}
        distributorConfiguration={formData}
        type={formData.afType}
        moduleName={formData.moduleName}
        listeners={formData.listeners}
        templateSection={template.listeners as FormTemplateSection}
        distributors={distributors}
        securityInfos={securityInfos}
        isEditing={isEditing}
        customValidationFunctionMap={customValidationFunctionMap}
        ipAddressOptions={nodeNameIpAddressOptions}
        i18nKeyPrefix={i18nKeyPrefix}
        onFieldUpdate={onFieldUpdate}
        onFieldCreateNew={onFieldCreateNew}
      />
      <Destinations
        ref={destinationRefs}
        templateSection={destinationsTemplate as FormTemplateSection}
        destinations={formData.destinations}
        securityInfos={securityInfos}
        isEditing={isEditing}
        type={formData.afType}
        moduleName={formData.moduleName}
        distributors={distributors}
        tlsProxies={tlsProxies}
        customValidationFunctionMap={customValidationFunctionMap}
        i18nKeyPrefix={i18nKeyPrefix}
        onFieldUpdate={onFieldUpdate}
        onFieldCreateNew={onFieldCreateNew}
      />
    </div>
  );
});

export default DistributorConfigForm;
